/**
 * Shared formatting utilities for AI panel components.
 * Used by AiConversationPanel.
 */

import { marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Format markdown text to safe HTML.
 *
 * Uses marked for full CommonMark rendering (tables, lists, headings, bold,
 * italic, inline code, blockquotes, links) and DOMPurify to sanitise the
 * resulting HTML before injection via v-html.
 *
 * @param {string} text - Raw markdown string from the AI assistant.
 * @returns {string} Sanitised HTML string, safe for v-html injection.
 */
export function formatMarkdown(text) {
  if (!text) return '';
  const html = marked.parse(text);
  // marked inserts \n between every block element. An ancestor in the Vuetify
  // component tree sets white-space: pre-line, which causes each \n to render
  // as a visible blank line. Strip inter-tag newlines at the source so the
  // inheritance problem cannot affect rendered output.
  // This is safe: marked HTML-entity-encodes content (> → &gt;, < → &lt;),
  // so literal ><  in the string is always structural, never user content —
  // with the one exception of <pre><code> blocks whose inner newlines are
  // not adjacent to a tag boundary and are therefore left intact.
  const compact = html.replace(/>\n</g, '><');
  return DOMPurify.sanitize(compact);
}

/**
 * Format an ISO timestamp for compact display, e.g. "15 Sep 13:45".
 *
 * @param {string} timestamp - ISO 8601 timestamp string
 * @returns {string}
 */
export function formatTimestamp(timestamp) {
  if (!timestamp) return '';

  try {
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day} ${month} ${hours}:${minutes}`;
  } catch {
    return '';
  }
}
